import React from 'react';
import styled, { css } from 'styled-components';
import { editorButtonTokens as tokens } from './editorButtonTokens';

export type EditorButtonVariant = 'primary' | 'secondary' | 'outline';

export interface EditorButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: EditorButtonVariant;
  iconOnly?: boolean;
}

const colorScheme = (background: string, foreground: string) => css`
  background-color: ${background};
  color: ${foreground};
`;

export const keyboardFocusRing = (color: string) => css`
  box-shadow: none;
  outline: 2px solid ${color};
  outline-offset: 2px;
`;

const primaryStyles = css`
  ${colorScheme(tokens.primary, tokens.primaryForeground)}

  &:hover {
    background-color: ${tokens.primaryActive};
  }

  &:focus-visible {
    background-color: ${tokens.primaryDeep};
    ${keyboardFocusRing(tokens.primaryDeep)}
  }

  &:active {
    background-color: ${tokens.primaryDeep};
  }

  &:disabled {
    ${colorScheme(tokens.primaryDisabled, tokens.neutralGrey1)}
  }
`;

const secondaryStyles = css`
  ${colorScheme(tokens.secondary, tokens.primary)}

  &:hover {
    color: ${tokens.primaryActive};
  }

  &:focus-visible {
    ${colorScheme(tokens.secondaryActive, tokens.primaryDeep)}
    ${keyboardFocusRing(tokens.primaryActive)}
  }

  &:active {
    ${colorScheme(tokens.secondaryActive, tokens.primaryDeep)}
  }

  &:disabled {
    ${colorScheme(tokens.secondaryDisabled, tokens.secondary)}
  }
`;

const outlineStyles = css`
  ${colorScheme('transparent', tokens.neutralGrey5)}
  border: 1px solid ${tokens.neutralGrey3};

  &:hover {
    color: ${tokens.neutralGrey4};
  }

  &:focus-visible {
    ${keyboardFocusRing(tokens.primary)}
  }

  &:active {
    color: ${tokens.neutralGrey5};
    border-color: ${tokens.primary};
    box-shadow: inset 0 0 0 1px ${tokens.primary};
  }

  &:disabled {
    color: ${tokens.neutralGrey3};
    border-color: ${tokens.neutralGrey3};
  }
`;

const variantStyles: Record<EditorButtonVariant, ReturnType<typeof css>> = {
  primary: primaryStyles,
  secondary: secondaryStyles,
  outline: outlineStyles
};

export const editorButtonBaseStyles = css`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: ${tokens.height};
  min-height: 0;
  margin: 0;
  padding: 8px 20px;
  border: 0;
  border-radius: ${tokens.radius};
  font-family: ${tokens.fontFamily};
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  font-variant-numeric: lining-nums proportional-nums;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s ease;

  svg {
    flex-shrink: 0;
    pointer-events: none;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
  }
`;

const iconOnlyStyles = css`
  width: ${tokens.height};
  padding: 0;
`;

const StyledEditorButton = styled.button<{
  $variant: EditorButtonVariant;
  $iconOnly: boolean;
}>`
  ${editorButtonBaseStyles}
  ${({ $variant }) => variantStyles[$variant]}
  ${({ $iconOnly }) => $iconOnly && iconOnlyStyles}
`;

export const EditorButton = React.forwardRef<
  HTMLButtonElement,
  EditorButtonProps
>(
  (
    { variant = 'secondary', iconOnly = false, type = 'button', ...props },
    ref
  ) => (
    <StyledEditorButton
      ref={ref}
      type={type}
      $variant={variant}
      $iconOnly={iconOnly}
      {...props}
    />
  )
);

EditorButton.displayName = 'EditorButton';
